import React, {
  forwardRef,
} from 'react';
import type {
  ButtonHTMLAttributes,
  ForwardedRef,
  MouseEvent,
  ReactNode,
  KeyboardEvent,
} from 'react';
import {
  cva,
} from 'class-variance-authority';

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children' | 'onClick' | 'onMouseDown' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'type'
>;

export type Props = NativeButtonProps & {
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'login' | 'text';
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'login';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  endIcon?: ReactNode;
};

// Create buttonVariants using cva for compatibility with UI components
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-[#363131] text-white hover:bg-[#303030] font-medium shadow-sm hover:shadow-md',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-lg',
        outline:
          'border border-gray-200 text-gray-700 hover:bg-gray-100 shadow-none',
        secondary:
          'bg-gray-100 text-gray-900 hover:bg-gray-200 shadow-md hover:shadow-lg',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        login: 'bg-[#363131] text-white hover:bg-[#303030] font-medium shadow-sm hover:shadow-md',
        text: 'text-primary hover:bg-primary/10 !gap-0',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        login: 'h-12 px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = forwardRef(({
  className,
  id,
  type = 'button',
  variant = 'default',
  size = 'default',
  onClick,
  onMouseDown,
  onFocus,
  onBlur,
  onKeyDown,
  disabled,
  children,
  endIcon,
  tabIndex,
}: Props, ref: ForwardedRef<HTMLButtonElement>) => {
  return (
    <button
      id={id}
      className={buttonVariants({ variant, size, className })}
      type={type}
      onClick={onClick}
      onMouseDown={onMouseDown}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      ref={ref}
    >
      {children}
      {endIcon && endIcon}
    </button>
  );
});

Button.displayName = 'Button';

// Export both named and default for compatibility
export { Button, buttonVariants };
export default Button;
